import { compareAsc } from 'date-fns'
import { TFunction } from 'i18next'
import { difference, get, groupBy, intersection } from 'lodash-es'
import moment from 'moment'

import { MultiLocaleTimeFormats } from '@/lib/datetime'
import {
	AirlineConfirmationInfo,
	Itinerary,
	ItineraryCarBooking,
	ItineraryFlightBooking,
	ItineraryHotelBooking,
	ItineraryRailBooking,
	PreTripRequest,
	Trip,
	TripFilterSet,
	TripPurpose,
	TripPurposeOption,
	WithBookingSegmentToTravelers,
	WithBookingType,
	WithCvvRequirement,
	WithItineraryBookingsManagedByUser,
	WithReservationStatus,
} from '@/travelsuit'
import { getFilteredSelectedSeatsParams } from '@/travelsuit/seatmaps'
import { fullName } from '@/travelsuit/users.utils'
import { CancellationStatus, UserId, WithCancellationStatus } from '@/types/common'
import { CreditCardType } from '@/types/creditCard'
import { ApiBookingErrorReason, ErrorsInBooking } from '@/types/errors'
import { FlightProvider } from '@/types/flights/common'
import { FlightAmenity } from '@/types/flights/search'
import { Location } from '@/types/location'
import { BookingSegmentId, ReservationStatus, TripStatus } from '@/types/trip'
import { User } from '@/types/user'

import { getBookingSegmentToTravelerError } from './getBookingSegmentToTravelerError'
import { getItineraryFailedServicesQuantity } from './getItineraryFailedServicesQuantity'
import { isFailedBooking } from './isFailedBooking'
import { getRailsDepartureDatetime } from './train-utils'
import { minMax } from './utils'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const tripFilterFns: { [k in keyof TripFilterSet]: (trip: Trip, value: any) => boolean } = {
	departingFrom: (trip, filter: Date) =>
		Boolean(
			trip.products.length &&
			trip.products[0].start_time &&
			moment.parseZone(trip.products[0].start_time).isSameOrAfter(filter),
		),
	departingTo: (trip, filter: Date) =>
		Boolean(
			trip.products.length &&
			trip.products[trip.products.length - 1].start_time &&
			moment.parseZone(trip.products[trip.products.length - 1].start_time).isSameOrBefore(filter),
		),
	destinationName: (trip, locationName: string) =>
		Boolean(
			trip.products.some(
				(d) =>
					(d.to_location && d.to_location.name === locationName) ||
					(d.from_location && d.from_location.name === locationName),
			),
		),
	traveler: (trip, traveler: User) => Boolean(trip.travelers.map((t) => t.id).indexOf(traveler.id) >= 0),
	creator: (trip, creator: User) => Boolean(trip.created_by === creator.id),
	office: (trip, office: Location) =>
		Boolean(trip.travelers.some((t) => get(t, 'traveler_profile.office.id', null) === office.id)),
}

export function isItineraryHasSelections(itinerary: Itinerary | null): boolean {
	return Boolean(
		itinerary &&
		[itinerary.flight_itinerary?.summary, itinerary.hotel_itinerary?.summary, itinerary.car_itinerary?.summary].some(
			(a) => Boolean(a?.length),
		),
	)
}

export function getTripRequestDates(
	request: PreTripRequest,
	{ format = MultiLocaleTimeFormats.FullReadableDate, sep = ' - ' }: Partial<{ format: string; sep: string }> = {},
) {
	const routeDates = request.route.reduce((all, r) => [...all, r.from_date, r.to_date], []).map((d) => moment(d).unix())
	return minMax(routeDates)
		.map((d) => moment.unix(d).format(format))
		.join(sep)
}

export function getFlightsBySearchId(itinerary: Itinerary | undefined, search_id: number) {
	return itinerary?.flights_bookings.filter((e) => e.search_id === search_id) ?? []
}

export function getHotelsBySearchId(itinerary: Itinerary | undefined, search_id: number) {
	return itinerary?.hotels_bookings.filter((e) => e.search_id === search_id) ?? []
}

export function getCarsBySearchId(itinerary: Itinerary | undefined, search_id: number) {
	return itinerary?.cars_bookings.filter((e) => e.search_id === search_id) ?? []
}

export function getGroupedFlights(itinerary: Itinerary): Record<string, ItineraryFlightBooking[]> {
	return itinerary?.flights_bookings.length
		? groupBy(itinerary.flights_bookings, (e) => [e.search_id, e.cancellation_status].map(String).join(';'))
		: {}
}

function getGroupedRails(itinerary: Itinerary | undefined): Record<string, ItineraryRailBooking[]> {
	return itinerary?.rails_bookings.length
		? groupBy(itinerary.rails_bookings, (e) => [e.search_id, e.cancellation_status].map(String).join(';'))
		: {}
}

export function getGroupedHotels(itinerary: Itinerary | undefined): Record<string, ItineraryHotelBooking[]> {
	return itinerary?.hotels_bookings.length
		? groupBy(itinerary!.hotels_bookings, (e) => [e.search_id, e.cancellation_status].map(String).join(';'))
		: {}
}

export function getGroupedCars(itinerary: Itinerary | undefined): Record<string, ItineraryCarBooking[]> {
	return itinerary?.cars_bookings.length // TODO fix key
		? groupBy<ItineraryCarBooking>(itinerary!.cars_bookings, (e) =>
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				[e.search_id, (e as any).cancellation_status].map(String).join(';'),
			)
		: {}
}

export function getSortedFlights(itinerary: Itinerary) {
	const flightsBySearchId = getGroupedFlights(itinerary)
	const sortedFlightKeys = Object.keys(flightsBySearchId).sort(
		(a, b) =>
			moment.parseZone(flightsBySearchId[a][0]!.flights[0].segments[0].departure_time).valueOf() -
			moment.parseZone(flightsBySearchId[b][0]!.flights[0].segments[0].departure_time).valueOf(),
	)

	return sortedFlightKeys.map((k) => flightsBySearchId[k])
}

export function getSortedRails(itinerary: Itinerary) {
	const railsBySearchId = getGroupedRails(itinerary)
	const sortedRailsKeys = Object.keys(railsBySearchId).sort((a, b) =>
		compareAsc(
			railsBySearchId[a][0] ? getRailsDepartureDatetime(railsBySearchId[a][0]) : 0,
			railsBySearchId[b][0] ? getRailsDepartureDatetime(railsBySearchId[b][0]) : 0,
		),
	)

	return sortedRailsKeys.map((k) => railsBySearchId[k])
}

export function getSortedHotels(itinerary: Itinerary) {
	const hotelsBySearchId = getGroupedHotels(itinerary)

	const sortedHotelKeys = Object.keys(hotelsBySearchId).sort(
		(a, b) =>
			moment.parseZone(hotelsBySearchId[a][0]!.check_in).valueOf() -
			moment.parseZone(hotelsBySearchId[b][0]!.check_in).valueOf(),
	)

	return sortedHotelKeys.map((k) => hotelsBySearchId[k])
}

export function getSortedCars(itinerary: Itinerary) {
	const carsBySearchId = getGroupedCars(itinerary)

	const sortedCarKeys = Object.keys(carsBySearchId).sort(
		(a, b) =>
			moment.parseZone(carsBySearchId[a][0]!.car?.start_time).valueOf() -
			moment.parseZone(carsBySearchId[b][0]!.car?.start_time).valueOf(),
	)

	return sortedCarKeys.map((k) => carsBySearchId[k])
}

export function isTripEditable(trip?: Trip): boolean {
	if (!trip || trip.status === TripStatus.NotApproved) {
		return false
	}
	return [TripStatus.Reverted, TripStatus.Draft, TripStatus.DraftBySupport].includes(trip.status)
}

function seatSelectionKeyExtractor(flightBookings: ItineraryFlightBooking[]): string[] {
	return flightBookings.flatMap((flightBooking) =>
		flightBooking.flights.flatMap((flight) =>
			flight.segments.flatMap((segment) => segment.seat_selection.flatMap((selection) => selection.seat_key || '')),
		),
	)
}

export function getSelectedSeats(itinerary: Itinerary, noSelectedSeats: boolean) {
	if (noSelectedSeats) {
		return { seat_keys: [] }
	}

	return { seat_keys: seatSelectionKeyExtractor(itinerary.flights_bookings) }
}

export function getFilteredSelectedSeats({
	itinerary,
	noSelectedSeats,
	selectedFlightId,
}: getFilteredSelectedSeatsParams) {
	if (noSelectedSeats) {
		return { seat_keys: [] }
	}
	return {
		seat_keys: seatSelectionKeyExtractor(
			itinerary.flights_bookings.filter((flightBooking) => Number(flightBooking.id) === Number(selectedFlightId)),
		),
	}
}

function isBookingCvvRequirementMissing(booking: WithCvvRequirement & WithBookingType) {
	return typeof booking.cvv_required !== 'boolean' && booking.booking_type !== 'external_booking'
}

export function areCvvRequirementsMissing(itinerary: Itinerary) {
	return [itinerary.flights_bookings, itinerary.hotels_bookings].flat().some(isBookingCvvRequirementMissing)
}

function isCvvRequiredForBooking({ cvv_required }: WithCvvRequirement) {
	return !!cvv_required
}

export function isSomeServiceRequiresCvv(itinerary: WithItineraryBookingsManagedByUser) {
	const { flights_bookings, hotels_bookings, cars_bookings } = itinerary
	// BCDTDRCT-11273: AC#2.2 Show CVV field for corporate and Traveler cards selected for trains payment if CVV was required for this card
	// rails_bookings never require CVC, check CreditCardResponse.cvc_exist instead
	return [...flights_bookings, ...hotels_bookings, ...cars_bookings].some(isCvvRequiredForBooking)
}

const POSSIBLE_CARD_TYPES: CreditCardType[] = [
	CreditCardType.Visa,
	CreditCardType.MasterCard,
	CreditCardType.AmericanExpress,
	CreditCardType.AmexBTA,
	CreditCardType.AmexBTAGermany,
	CreditCardType.Diners,
	CreditCardType.AirPlus,
]

export function getUnsupportedCardTypes(itinerary: WithItineraryBookingsManagedByUser): CreditCardType[] {
	// TODO: Refactor, move this logic to BE
	// https://entdevbcdtravel.atlassian.net/browse/BCDTDRCT-10413

	const sabreFlights =
		itinerary.flights_bookings?.filter((item) => item.vendor?.toLowerCase?.() === FlightProvider.Sabre) ?? []
	const allBookings = [
		...(itinerary.hotels_bookings ?? []),
		...(itinerary.rails_bookings ?? []),
		...(sabreFlights ?? []),
	]

	const acceptedCardsList = allBookings.reduce<CreditCardType[][]>((acc, booking) => {
		const cards = booking.payment_cards_accepted ?? []
		if (cards.length > 0) {
			acc.push(cards)
		}
		return acc
	}, [])

	if (acceptedCardsList.length === 0) {
		return []
	}

	const commonAcceptedCards = acceptedCardsList.length > 0 ? intersection(...acceptedCardsList) : []
	return difference(POSSIBLE_CARD_TYPES, commonAcceptedCards)
}
export function orderTripPurposeOptions({
	tripPurposeOptions,
	tripPurposes,
}: {
	tripPurposeOptions: TripPurposeOption[]
	tripPurposes: TripPurpose[]
}) {
	const selectedTripPurposeOptions = tripPurposeOptions.filter((p) => tripPurposes.includes(p.value))
	return [...selectedTripPurposeOptions.filter((p) => p.value !== TripPurpose.Other)]
}

export function getBookingInfoFromServiceItinerary<T>(
	itineraryService: { summary: { booking_info: T[] }[] } | undefined,
) {
	return itineraryService?.summary.flatMap((bookingSummary) => bookingSummary.booking_info) ?? []
}

export function getHotelBookingInfoFromItineraryById(itinerary?: Itinerary, withHotelId: { hotel_id?: string } = {}) {
	const hotelId = withHotelId.hotel_id
	if (!itinerary || !hotelId) {
		return undefined
	}

	return getBookingInfoFromServiceItinerary(itinerary.hotel_itinerary).find(({ hotel }) => hotel.id === hotelId)
}

export function getTravelerBSTT<T extends WithBookingSegmentToTravelers>(booking: T, travelerId: UserId) {
	return booking.booking_segment_to_travelers.find((bstt) => bstt.user_id === travelerId)
}

export function getConfirmationDetailsFromSegments<
	T extends { travelers_ids: UserId[]; booking_segment_id: BookingSegmentId },
	Y extends { confirmationNumber?: string | null; airlineConfirmationNumbers?: AirlineConfirmationInfo[] },
>(
	{ id, travelers }: { id: number | string; travelers: User[] },
	{ summary }: { summary: T[] },
	getConfirmationDetailsForTraveler: (summary: T, travelerId: number) => Y,
) {
	return summary
		.filter((details) => details.booking_segment_id === Number(id))
		.flatMap((details) =>
			details.travelers_ids.map((travelerId) => {
				const traveler = travelers.find((traveler) => traveler.id === travelerId)
				if (!traveler) {
					return null
				}

				const confirmationDetails = getConfirmationDetailsForTraveler(details, travelerId)

				return {
					id: travelerId,
					fullName: fullName(traveler),
					...confirmationDetails,
				}
			}),
		)
		.filter(
			(confirmationDetails): confirmationDetails is Y & { id: number; fullName: string; confirmationNumber: string } =>
				!!confirmationDetails,
		)
}

export function isSuccessBookingStatus({
	reservation_status,
}: {
	reservation_status?: ReservationStatus | null
}): boolean {
	return !!reservation_status && [ReservationStatus.Booked, ReservationStatus.PreBooked].includes(reservation_status)
}

export function isBookingSegmentActive(bookingSegment: WithCancellationStatus) {
	return (
		bookingSegment.cancellation_status === null ||
		bookingSegment.cancellation_status === CancellationStatus.CancellationFailed
	)
}

export function getSegmentMeal(seg: { amenities: FlightAmenity[] | null }): boolean {
	return (seg.amenities || []).some((amenity) => amenity.amenity === 'fresh_food' && amenity.exists)
}

export function isTripFailed(trip: Trip, itinerary: Itinerary) {
	return (
		trip.status === TripStatus.Reverted ||
		(trip.status === TripStatus.Draft && getItineraryFailedServicesQuantity(itinerary) > 0)
	)
}

const PAYMENT_DECLINE_PATTERNS = [
	'3ds',
	'three ds',
	'payment hold failed',
	'hold product fee failed',
	'hold failed',
	'payment declined',
	'card declined',
	'credit card issue',
	'authentication failed',
	'declined',
]

const PAYMENT_DECLINE_ERROR_TYPES: ApiBookingErrorReason[] = [
	ApiBookingErrorReason.CreditCardIssue,
	ApiBookingErrorReason.CompletionFailed3DS,
	ApiBookingErrorReason.HoldProductFeeFailed,
]

export function isPaymentDeclinedError(error?: Partial<ErrorsInBooking> | null): boolean {
	if (!error) {
		return false
	}

	if (error.error_type && PAYMENT_DECLINE_ERROR_TYPES.includes(error.error_type)) {
		return true
	}

	if (error.error_type && error.error_type !== ApiBookingErrorReason.UnknownError) {
		return false
	}

	const textToSearch = [error.reason, error.message, error.user_error, error.dev_message, error.error_code]
		.filter(Boolean)
		.join(' ')
		.toLowerCase()
		.replace(/_/g, ' ')

	return PAYMENT_DECLINE_PATTERNS.some((pattern) => textToSearch.includes(pattern))
}

function getFailedBookingTitle(booking: WithBookingSegmentToTravelers, t: TFunction): string {
	const errors = (booking.booking_segment_to_travelers ?? []).map(getBookingSegmentToTravelerError)

	if (errors.some(isPaymentDeclinedError)) {
		return t('itinerary-submit.payment-declined')
	}
	if (errors.some((e) => e?.error_type === ApiBookingErrorReason.PassportDataRequiredError)) {
		return t('booking-status.modal.title.passport-required-alert')
	}
	return t('booking-status.modal.title.alert')
}

export function getFailedBookingInfo({
	booking,
	isAlternativeFaresImprovementsEnabled,
	t,
}: {
	booking?: (Partial<WithReservationStatus> & WithBookingSegmentToTravelers) | null
	isAlternativeFaresImprovementsEnabled?: boolean
	t: TFunction
}) {
	const isBookingFailed = Boolean(isAlternativeFaresImprovementsEnabled && isFailedBooking(booking ?? undefined))
	const failedTitle = isBookingFailed && booking ? getFailedBookingTitle(booking, t) : undefined

	return { isBookingFailed, failedTitle }
}
